import { useQuery } from '@tanstack/react-query';
import { MeetingTemplateEditor, type MeetingTemplateDraft } from '@wundo/ui';
import { fetchMeetingTemplate } from '@wundo/client/memberMeetingTemplates.ts';
import type { CohortOption } from '@/lib/cohorts.ts';
import { meetingTemplateLabels } from '@/lib/meetingTemplateLabels.ts';
import type { TrackOption } from '@/lib/tracks.ts';
import { m } from '@/paraglide/messages.js';

interface MeetingTemplateEditScreenProps {
  userId: string;
  templateId: string;
  /** What an organisation's template can be aimed at. */
  tracks: TrackOption[];
  cohorts: CohortOption[];
  saving: boolean;
  /** Ready to show under the buttons. */
  error: string | undefined;
  onSave: (draft: MeetingTemplateDraft) => void;
  onDelete: (templateId: string) => Promise<void>;
  onCancel: () => void;
}

/** One existing gesprekstype, read in full before it is shown. */
export function MeetingTemplateEditScreen({
  userId,
  templateId,
  tracks,
  cohorts,
  saving,
  error,
  onSave,
  onDelete,
  onCancel,
}: MeetingTemplateEditScreenProps) {
  const { data: editing } = useQuery({
    queryKey: ['meetingTemplate', userId, templateId],
    queryFn: () => fetchMeetingTemplate(userId, templateId),
  });

  /* The detail is read before it can be shown; until then the list stands. */
  if (!editing) return null;

  return (
    <MeetingTemplateEditor
      /* Remounts on another template, so the form starts from that one. */
      key={editing.id}
      template={{
        ...editing,
        trackId: editing.track?.id ?? null,
        cohortId: editing.cohort?.id ?? null,
      }}
      kind={editing.kind}
      /* A personal one has no audience, and a default stays everyone's. */
      tracks={editing.scope === 'organisation' ? tracks : []}
      cohorts={editing.scope === 'organisation' ? cohorts : []}
      wholeOrganisationOnly={editing.is_default}
      /* Whoever may change this template edits it, everybody else reads it. */
      readOnly={!editing.editable}
      canDelete
      /* Every kind always has a default to fall back to. */
      deleteRefusal={
        editing.is_default ? m.settings_template_delete_default_note() : undefined
      }
      saving={saving}
      error={error}
      onSave={onSave}
      onDelete={() => onDelete(editing.id)}
      onCancel={onCancel}
      labels={meetingTemplateLabels()}
    />
  );
}
